import { QUERY_KEY_COMBINATIONS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import useQueryClientCallback from '@/hooks/useQueryClientCallback'
import { updateServiceType } from '@/modules/Service/services'
import { Service } from '@/modules/Service/types'
import { ModalProps } from '@/types/shared'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, GridHookForm } from 'vnetwork-ui'

export interface EditServiceTypeModalProps extends ModalProps {
    serviceID: number
    service: Service
}

interface FormValues {
    service_type: string
}

const EditServiceTypeModal: React.FC<EditServiceTypeModalProps> = ({
    open,
    onClose,
    serviceID,
    service,
}) => {
    const { t } = useTranslation()
    const { refetch } = useQueryClientCallback({
        pattern: 'services',
        mode: 'contains',
    })
    const { refetch: refetchTickets } = useQueryClientCallback({
        pattern: 'tickets',
        mode: 'contains',
    })

    const formControl = useForm<FormValues>({
        defaultValues: {
            service_type: service?.service_type || 'new-selling',
        },
    })

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: ({
                serviceID,
                data,
            }: {
                serviceID: number
                data: FormValues
            }) =>
                updateServiceType({
                    serviceID,
                    service_type: data.service_type,
                }),
        },
        {
            successMessage: t('update-service-type-success'),
            reactQueryKeys: QUERY_KEY_COMBINATIONS.SERVICE_WITH_TICKET,
            onSuccess: () => {
                refetch()
                refetchTickets()
                onClose()
            },
        }
    )

    const handleSubmit = async (values: FormValues) => {
        mutateAsync({ serviceID, data: values })
    }

    return (
        <Dialog open={open} onClose={onClose} title={t('edit-service-type')}>
            <GridHookForm
                onSubmit={handleSubmit}
                actions={[
                    {
                        title: t('cancel'),
                        type: 'button',
                        handler: onClose,
                        disabled: isPending,
                        variant: 'outlined',
                    },
                    {
                        title: t('submit'),
                        type: 'submit',
                        disabled: isPending || !formControl?.formState?.isDirty,
                        loading: isPending,
                        variant: 'contained',
                    },
                ]}
                data={[
                    {
                        formItemProps: {
                            name: 'service_type',
                            type: 'select',
                            options: [
                                {
                                    label: t('service_type.new-selling'),
                                    value: 'new-selling',
                                },
                                {
                                    label: t('service_type.upsale'),
                                    value: 'upsale',
                                },
                            ],
                        },
                        className: 'col-span-4',
                    },
                ]}
                formControl={formControl}
            />
        </Dialog>
    )
}

export default EditServiceTypeModal
